import React, {
  createContext,
  useCallback,
  useContext,
  useState
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import deepmerge from 'deepmerge';
import { useTranslation } from 'react-i18next';

import { Logger } from 'wtb-utils';
import { ANALYTICS_EVENTS, APP_STEPS } from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';

const AppContext = createContext();

const defaultState = {
  isLoading: true,
  furtherStep: 0,
  errorDialogCode: null,
  errorDialogTitle: null,
  errorDialogMessage: null,
  isEmailValidationSnackbarOpen: false
};

const APP_STEP_ORDER = [
  APP_STEPS.ORDER_SUMMARY,
  APP_STEPS.PERSONAL_INFORMATION,
  APP_STEPS.PAYMENT
];

const AppProvider = ({ children, initialState: state = {} }) => {
  const { pathname } = useLocation();
  const { id } = useParams();
  const history = useHistory();

  const { i18n } = useTranslation();

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const initialState = deepmerge(defaultState, state);

  const [ isLoading, setIsLoading ] = useState(initialState.isLoading);
  const [ errorDialogCode, setErrorDialogCode ] = useState(initialState.errorDialogCode);
  const [ errorDialogTitle, setErrorDialogTitle ] = useState(initialState.errorDialogTitle);
  const [ errorDialogMessage, setErrorDialogMessage ] = useState(initialState.errorDialogMessage);
  const [ furtherStep, setFurtherStep ] = useState(initialState.furtherStep);
  const [ appErrorType, setAppErrorType ] = useState('');
  const [ errorRedirectUrl, setErrorRedirectUrl ] = useState('');
  const [
    isEmailValidationSnackbarOpen, setIsEmailValidationSnackbarOpen
  ] = useState(initialState.isEmailValidationSnackbarOpen);

  const setIsLoadingTimeout = useCallback((value) => {
    const timeout = setTimeout(() => {
      if (!value) {
        if (!i18n.language) {
          clearTimeout(timeout);
          setIsLoadingTimeout(value);
        } else {
          setIsLoading(value);
        }
      } else {
        setIsLoading(value);
      }
    }, 100);
  }, [ setIsLoading, i18n.language ]);

  const isSuccess = useCallback(() => pathname.includes('/success'), [ pathname ]);
  const isError = useCallback(() => pathname.includes('/error'), [ pathname ]);
  const isDownload = useCallback(() => pathname.includes('/download'), [ pathname ]);

  const openErrorDialog = (code = 500, title, message) => {
    setErrorDialogCode(code);
    setErrorDialogTitle(title);
    setErrorDialogMessage(message);
  };
  const closeErrorDialog = () => {
    setErrorDialogCode(null);
    setErrorDialogTitle(null);
    setErrorDialogMessage(null);
  };

  const setStep = (step) => {
    if (!step) {
      Logger.error('Step is necessary to change step');
      return;
    }

    if (step === APP_STEPS.ERROR) {
      window.sessionStorage.setItem('manual-error', true);
    }

    if (!id) {
      if (step === APP_STEPS.ERROR) {
        history.push(`/${APP_STEPS.ERROR}`);
      }

      return;
    }

    setFurtherStep((prev) => {
      const stepPosition = APP_STEP_ORDER.indexOf(step) + 1;

      if (stepPosition === 0) {
        return APP_STEP_ORDER.length;
      } else if (stepPosition > prev) {
        return stepPosition;
      } else {
        return prev;
      }
    });

    history.push(`/${id}/${step}`);

    // Send corresponding analytics event
    switch (step) {
      case APP_STEPS.ORDER_SUMMARY:
        sendAnalyticsEvent(ANALYTICS_EVENTS.ORDER_SUMMARY_STEP);
        break;
      case APP_STEPS.PERSONAL_INFORMATION:
        sendAnalyticsEvent(ANALYTICS_EVENTS.PERSONAL_INFORMATION_STEP);
        break;
      case APP_STEPS.PAYMENT:
        sendAnalyticsEvent(ANALYTICS_EVENTS.SELECT_PAYMENT_PLAN_STEP);
        break;
      default: break;
    }
  };

  return (
    <AppContext.Provider
      value={{
        isLoading,
        setIsLoading, // : setIsLoadingTimeout,
        isSuccess, isError, isDownload,
        errorDialogCode, setErrorDialogCode,
        errorDialogTitle, setErrorDialogTitle,
        errorDialogMessage, setErrorDialogMessage,
        openErrorDialog, closeErrorDialog,
        setAppStep: setStep,
        furtherStep,
        appErrorType,
        setAppErrorType,
        errorRedirectUrl, setErrorRedirectUrl,
        isEmailValidationSnackbarOpen, setIsEmailValidationSnackbarOpen
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const AppConsumer = AppContext.Consumer;

const useAppContext = () => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error('useAppContext must be used within a AppProvider');
  }

  return context;
};

export {
  AppContext, AppProvider, AppConsumer, useAppContext
};
